import { authorizedBillingFetch } from 'app/axios/billing'
import { authorizedEdgeFetch } from 'app/axios/edge'

import { EDGE_API_URL, EDGE_CONFIG_URL, EDGE_SOFTWARE_URL } from 'app/consts'
import { HomeActiveConfig } from './types'

export const fetchHome = (homeId: string) =>
  authorizedEdgeFetch.get(`/rms/homes/${homeId}`)

export interface HomeActiveConfigParams {
  type?: HomeActiveConfig['type']
  redirect?: boolean
  lastactive?: boolean
}

export interface SubscriptionCreditsParams {
  after?: number
}

export const fetchSubscriptions = (homeId: string) =>
  authorizedBillingFetch.get(`/home/${homeId}/subscriptions`)

export const fetchHomeSubscriptionPrice = (homeId: string) =>
  authorizedBillingFetch.get(`/home/${homeId}/subscription/prices`)

export const fetchFullHomeSubscriptionPrice = (homeId: string) =>
  authorizedBillingFetch.get(`/v2/home/${homeId}/subscription/prices`)

export const fetchHomeSubscriptionPreview = (homeId: string) =>
  authorizedBillingFetch.get(`/home/${homeId}/subscriptions/preview`)

export const fetchSubscriptionPrice = (homeId: string) =>
  authorizedBillingFetch.get(`/v2/home/${homeId}/subscription/prices`)

export const cancelActiveSubscription = (
  rootDomainId: string,
  homeId: string
) =>
  authorizedBillingFetch.put(
    `/root-domain/${rootDomainId}/home/${homeId}/subscriptions`
  )

export const fetchSubscriptionHistory = (
  rootDomainId: string,
  homeId: string
) =>
  authorizedBillingFetch.get(
    `root-domain/${rootDomainId}/home/${homeId}/subscriptions/history`
  )

export const fetchSubscriptionCredits = (
  homeId: string,
  params?: SubscriptionCreditsParams
) =>
  authorizedBillingFetch.get(`/home/${homeId}/subscription/credits`, {
    params,
  })

export const fetchHomeActiveConfig = (
  homeId: string,
  params?: HomeActiveConfigParams
) => authorizedEdgeFetch.get(`/homes/${homeId}/config/active`, { params })


export const fetchHomeEnergyHistory = (
  homeId: string,
  body: EnergyHistoryPayload
) =>
  authorizedEdgeFetch.put(`/homes/${homeId}/message/energyDataUpload`, body)

export interface AvailableHomeSoftwareParams {
  location?: boolean
}

export const fetchAvailableHomeSoftware = (
  homeId: string,
  params?: AvailableHomeSoftwareParams
) =>
  authorizedEdgeFetch.get(`/home/${homeId}/host/available`, {
    baseURL: EDGE_SOFTWARE_URL,
    params,
  })

export const featchHomeSoftwareHistory = (homeId: string) =>
  authorizedEdgeFetch.get(`/rms/homes/${homeId}/softwarehistory`, {
    baseURL: EDGE_API_URL,
  })

export interface InstallSoftwareUpdateBody {
  componentSoftwareId: string
  clearConfig: boolean
}

export interface EnergyHistoryPayload {
  payload:{
    bucket: string
    startTime: string
    endTime: string
  }
}

export const installHomeSoftwareUpdate = (
  homeId: string,
  partNumber: string,
  body: InstallSoftwareUpdateBody,
  setPercentProgress?: React.Dispatch<React.SetStateAction<number>>
) =>
  authorizedEdgeFetch.post(`/home/${homeId}/${partNumber}/update`, body, {
    baseURL: EDGE_SOFTWARE_URL,
    onUploadProgress: (progress) => {
      setPercentProgress &&
        setPercentProgress(Math.round((progress.loaded * 100) / progress.total))
    },
  })

export interface HomeCloudSoftwareHistoryParams {
  next?: string
}

export const fetchHomeCloudSoftwareHistory = (
  homeId: string,
  partNumber: string,
  params?: HomeCloudSoftwareHistoryParams
) =>
  authorizedEdgeFetch.get(`/history/home/${homeId}/part/${partNumber}`, {
    baseURL: EDGE_SOFTWARE_URL,
    params,
  })

export const getSoftwareUpdateStatus = (homeId: string, updateId: string) =>
  authorizedEdgeFetch.get(`/status/home/${homeId}/update/${updateId}`, {
    baseURL: EDGE_SOFTWARE_URL,
  })

export const deleteDomainConfigFile = (configId: string) =>
  authorizedEdgeFetch.delete(`/delete/${configId}`, {
    baseURL: EDGE_CONFIG_URL,
  })

export interface DeployDomainConfigFileBody {
  config_id: string
  home_id: string
  integrator_id: string
  version?: string
}

export const deployDomainConfigFile = (
  body: DeployDomainConfigFileBody,
  setPercentProgress: React.Dispatch<React.SetStateAction<number>>
) =>
  authorizedEdgeFetch.post(`/push`, body, {
    baseURL: EDGE_CONFIG_URL,
    onUploadProgress: (progress) => {
      setPercentProgress(Math.round((progress.loaded * 100) / progress.total))
    },
  })

export interface FetchAvailableLicensesProps {
  domainId: string
  homeId: string
}

export const fetchAvailableLicenses = ({
  domainId,
  homeId,
}: FetchAvailableLicensesProps) =>
  authorizedEdgeFetch.get(
    `/rms/domains/${domainId}/homes/${homeId}/licenses/available`
  )

export interface ApplyDomainLicenseToHomeProps {
  domainId: string
  homeId: string
  licenseId: number
}

export const homeApplyLicense = ({
  domainId,
  homeId,
  licenseId,
}: ApplyDomainLicenseToHomeProps) =>
  authorizedEdgeFetch.put(
    `/rms/domains/${domainId}/homes/${homeId}/licenses/${licenseId}`
  )

export const fetchHomeUsersUber = (homeId: string) =>
  authorizedEdgeFetch.get(`/homes/${homeId}/users/uber`)

export interface InviteUserBody {
  email: string
  firstName: string
  lastName: string
}

export const inviteHomeUser = (homeId: string, body: InviteUserBody) =>
  authorizedEdgeFetch.post(`/homes/${homeId}/invitations`, body)

export interface RequestAccessBody {
  configAccess?: number
  monitorAccess?: boolean
}

export const requestAccess = (
  domainId: string,
  homeId: string,
  body?: RequestAccessBody
) =>
  authorizedEdgeFetch.put(
    `/rms/domains/${domainId}/homes/${homeId}/requestaccess`,
    body
  )
export const getPartners = (homeId: string) =>
  authorizedEdgeFetch.get(`/homes/${homeId}/partners`)

export const getPartnersInbound = (homeId: string) =>
  authorizedEdgeFetch.get(`/homes/${homeId}/partners/inbound`)

interface SubscriptionItem {
  priceId: string
  quantity: number
}

export interface PurchaseSubscriptionParams {
  homeId: string
  rootDomainId: string
  paymentMethodId?: string
  couponId?: string
  type: string
  items: SubscriptionItem[]
}

export interface UpdateSubscriptionParams {
  rootDomainId: string
  homeId: string
  paymentMethodId: string
}

export const purchaseSubscription = (params: PurchaseSubscriptionParams) =>
  authorizedBillingFetch.post(
    `v2/root-domain/${params.rootDomainId}/home/${params.homeId}/subscriptions`,
    {
      paymentMethodId: params.paymentMethodId,
      couponId: params.couponId,
      type: params.type,
      items: params.items,
    }
  )

export const previewSubscription = (params: PurchaseSubscriptionParams) =>
  authorizedBillingFetch.post(
    `v2/root-domain/${params.rootDomainId}/home/${params.homeId}/subscriptions/preview`,
    {
      paymentMethodId: params.paymentMethodId,
      couponId: params.couponId,
      type: params.type,
      items: params.items,
    }
  )

export const migrateSubscription = (params: UpdateSubscriptionParams) =>
  authorizedBillingFetch.post(
    `/root-domain/${params.rootDomainId}/home/${params.homeId}/subscriptions/migrate`,
    {
      paymentMethodId: params.paymentMethodId,
    }
  )

export const renewSubscription = (params: UpdateSubscriptionParams) =>
  authorizedBillingFetch.put(
    `/root-domain/${params.rootDomainId}/home/${params.homeId}/subscription/renew`,
    {
      paymentMethodId: params.paymentMethodId,
    }
  )
export const fetchHomeConfig = (homeId: string) =>
  authorizedEdgeFetch.get(`/homes/${homeId}/config`)

export const fetchHomePeriodEnd = (homeId: string) =>
  authorizedBillingFetch.get(`/home/${homeId}/subscription/period_end`)

export interface HostPasswordChangeParams {
  homeId: string
  payload: {
    oldPassword: string
    newPassword: string
  }
}
export const hostPasswordChange = (params: HostPasswordChangeParams) =>
  authorizedEdgeFetch.post(
    `/homes/${params.homeId}/password/change`,
    params.payload
  )

export const hostPasswordReset = (params: HostPasswordChangeParams) =>
  authorizedEdgeFetch.post(`/homes/${params.homeId}/password/reset`, {})
